import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useLang } from "@/i18n/LanguageContext";
import { IMG } from "@/lib/images";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Waitlist = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [state, setState] = useState({ loading: false, ok: false, err: "" });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      setState({ loading: false, ok: false, err: t.errors.email });
      return;
    }
    setState({ loading: true, ok: false, err: "" });
    try {
      await axios.post(`${API}/waitlist`, { ...form, locale: lang, source: "waitlist" });
      setState({ loading: false, ok: true, err: "" });
      setForm({ first_name: "", email: "" });
    } catch (err) {
      setState({ loading: false, ok: false, err: t.errors.generic });
    }
  };

  const titleLines = t.waitlist.title.split("\n");

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-28 md:py-44 text-ivory overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={IMG.sunset}
          alt="Sunset over Norwegian water"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(15,38,29,0.55)] via-[rgba(15,38,29,0.65)] to-[rgba(15,38,29,0.85)]" />
        <div className="absolute inset-0 grain" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="max-w-3xl"
        >
          <motion.p variants={fadeUp} className="overline text-bronze mb-6" data-testid="waitlist-eyebrow">
            {t.waitlist.eyebrow}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-serif text-4xl sm:text-5xl lg:text-[5rem] font-light leading-[1.05] tracking-tight"
            data-testid="waitlist-title"
          >
            {titleLines.map((l, i) => (
              <span key={i} className="block">
                {l}
              </span>
            ))}
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-8 text-base md:text-lg text-ivory/80 leading-relaxed max-w-xl"
            data-testid="waitlist-body"
          >
            {t.waitlist.body}
          </motion.p>

          <motion.form
            variants={fadeUp}
            onSubmit={submit}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 max-w-2xl"
            data-testid="waitlist-form"
          >
            <div>
              <label className="overline text-ivory/60 block mb-2">{t.waitlist.first_name}</label>
              <input
                data-testid="waitlist-firstname-input"
                type="text"
                value={form.first_name}
                onChange={onChange("first_name")}
                className="input-line input-line-dark"
                required
              />
            </div>
            <div>
              <label className="overline text-ivory/60 block mb-2">{t.waitlist.email}</label>
              <input
                data-testid="waitlist-email-input"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="input-line input-line-dark"
                required
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-6 mt-4">
              <button
                type="submit"
                disabled={state.loading}
                className="btn-bronze overline px-8 py-4 disabled:opacity-50"
                data-testid="waitlist-submit"
              >
                {state.loading ? "..." : t.waitlist.cta}
              </button>
              {state.ok && (
                <span className="overline text-bronze flex items-center gap-2" data-testid="waitlist-success">
                  <Check size={14} /> {t.waitlist.success}
                </span>
              )}
              {state.err && (
                <span className="overline text-bronze" data-testid="waitlist-error">
                  {state.err}
                </span>
              )}
            </div>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
};
